import React, { createContext, useContext, ReactNode } from 'react';
import { useTenant } from './TenantContext';
import { useMenuTranslations } from '../hooks/useMenuTranslations';

// Wrapper del TenantContext che applica automaticamente le traduzioni
const TranslatedTenantContext = createContext<any>(null);

export const TranslatedTenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const originalTenantCtx = useTenant();
    const { getTranslatedCategoryName, getTranslatedMenuItemName, getTranslatedMenuItemDescription, currentLanguage } = useMenuTranslations();

    // Applica le traduzioni ai dati del tenant
    const translatedTenantCtx = React.useMemo(() => {

        if (!originalTenantCtx) return originalTenantCtx;

        const { categories, subcategories, menuData, ...rest } = originalTenantCtx as any;

        // Traduci le categorie
        const translatedCategories = categories?.map((category: any) => {
            const translatedName = getTranslatedCategoryName(category.name);

            return {
                ...category,
                name: translatedName
            };
        }) || [];

        // Traduci le sottocategorie
        const translatedSubcategories = subcategories?.map((subcategory: any) => ({
            ...subcategory,
            name: getTranslatedCategoryName(subcategory.name)
        })) || [];

        // Traduci i dati del menu
        const translatedMenuData = menuData?.map((item: any) => {
            const translatedName = getTranslatedMenuItemName(item.nome);
            const translatedDesc = getTranslatedMenuItemDescription(item.descrizione || '');
            const translatedVariants = Array.isArray(item.variants)
                ? item.variants.map((variant: any) => ({
                    ...variant,
                    name: getTranslatedMenuItemName(variant.name || '')
                }))
                : item.variants;

            return {
                ...item,
                nome: translatedName,
                descrizione: translatedDesc,
                variants: translatedVariants
            };
        }) || [];

        return {
            ...rest,
            categories: translatedCategories,
            subcategories: translatedSubcategories,
            menuData: translatedMenuData
        };
    }, [originalTenantCtx, getTranslatedCategoryName, getTranslatedMenuItemName, getTranslatedMenuItemDescription, currentLanguage]);

    return (
        <TranslatedTenantContext.Provider value={translatedTenantCtx}>
            {children}
        </TranslatedTenantContext.Provider>
    );
};

export const useTranslatedTenant = () => {
    const context = useContext(TranslatedTenantContext);
    if (!context) {
        throw new Error('useTranslatedTenant must be used within a TranslatedTenantProvider');
    }
    return context;
};